import { useEffect } from 'react'
import { supabase } from '../lib/supabase'

/** Intervalo do heartbeat. O servidor ainda trava em 2 min (touch_last_active). */
export const ACTIVITY_HEARTBEAT_MS = 5 * 60 * 1000
const HEARTBEAT_SLACK_MS = 30 * 1000

/**
 * Decide se o heartbeat deve registrar uso agora.
 *
 * Chamada em dois momentos: no tick do intervalo e quando a aba volta a ficar
 * visível. A primeira marca da sessão (app aberto) não passa por aqui — é
 * sempre registrada, porque é justamente a visita curta de quem já estava
 * logado que o `ultimoAcesso` antigo perdia.
 *
 * @param lastTouchAt  epoch ms da última chamada à RPC nesta aba
 * @param now          epoch ms de agora
 * @param hidden       document.hidden (aba em segundo plano)
 */
export function shouldTouch(lastTouchAt: number, now: number, hidden: boolean): boolean {
  // Aba em segundo plano não é uso — o professor pode deixar o app aberto o dia todo.
  if (hidden) return false
  // Folga para o jitter do setInterval: um tick que chega alguns ms antes dos
  // 5 min não pode ser descartado, senão o registro só sai no tick seguinte
  // (10 min). O `true` a mais é barato: o servidor ainda trava em 2 min.
  return now - lastTouchAt >= ACTIVITY_HEARTBEAT_MS - HEARTBEAT_SLACK_MS
}

/**
 * Registra o "último uso" (profiles.last_active_at) enquanto o usuário está
 * no app com a aba visível. Fire-and-forget: falha de rede nunca afeta a UI.
 */
export function useActivityHeartbeat(userId: string | undefined) {
  useEffect(() => {
    if (!userId) return

    let lastTouchAt = 0

    const touch = () => {
      lastTouchAt = Date.now()
      supabase.rpc('touch_last_active').then(
        () => {},
        () => {}
      )
    }

    const maybeTouch = () => {
      if (shouldTouch(lastTouchAt, Date.now(), document.hidden)) touch()
    }

    if (!document.hidden) touch()

    const interval = setInterval(maybeTouch, ACTIVITY_HEARTBEAT_MS)
    const onVisibility = () => {
      if (!document.hidden) maybeTouch()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [userId])
}
